import React from 'react';
import { Button, Stack } from '@chakra-ui/react';
import {
  Archive,
  ArrowsClockwise,
  CheckCircle,
  DownloadSimple,
  Eye,
  FileDoc,
  PaperPlaneTilt,
  ShieldCheck,
} from 'phosphor-react';
import { EstatutoCardActions } from '../hooks/useEstatutoCardActions';

interface EstatutoAcoesSlotProps {
  actions: EstatutoCardActions;
}

/**
 * Botões de ação do estatuto (portados do antigo DrawerFooter), renderizados
 * como coluna vertical na SIDEBAR de 260px do modal genérico. Mesmo gating por
 * `acoes` / `canFinalizar` e mesmas mutations do hook.
 */
export const EstatutoAcoesSlot: React.FC<EstatutoAcoesSlotProps> = ({ actions }) => {
  const {
    estatuto,
    acoes,
    dados,
    downloadingDocx,
    openAssinatura,
    openPdf,
    marcarMutation,
    validarMutation,
    syncMutation,
    arquivarMutation,
    handleDownloadDocx,
    handleArquivar,
    canFinalizar,
    onFinalizar,
    finalizando,
  } = actions;

  if (!estatuto) return null;

  // Defesa contra status sem entrada em ACOES_POR_STATUS (ex.: enum novo no backend
  // ainda não propagado pro front) — sem isso, `acoes` vem undefined e todo
  // `.includes` abaixo quebra o render do modal inteiro.
  const acoesSeguras = acoes ?? [];

  return (
    <Stack spacing={2}>
      {acoesSeguras.includes('marcar_link_enviado') && (
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<PaperPlaneTilt size={14} weight="bold" />}
          onClick={() => marcarMutation.mutate()}
          isLoading={marcarMutation.isPending}
        >
          {estatuto.email_presidente ? 'Enviar link por e-mail' : 'Marcar link enviado'}
        </Button>
      )}
      {acoesSeguras.includes('validar') && (
        <Button
          size="sm"
          colorScheme="green"
          leftIcon={<ShieldCheck size={14} weight="bold" />}
          onClick={() => validarMutation.mutate()}
          isLoading={validarMutation.isPending}
        >
          Validar dados
        </Button>
      )}
      {acoesSeguras.includes('enviar_assinatura') && (
        <Button
          size="sm"
          colorScheme="purple"
          leftIcon={<PaperPlaneTilt size={14} weight="bold" />}
          onClick={openAssinatura}
        >
          Enviar para assinatura
        </Button>
      )}
      {acoesSeguras.includes('sync_autentique') && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<ArrowsClockwise size={14} weight="bold" />}
          onClick={() => syncMutation.mutate()}
          isLoading={syncMutation.isPending}
        >
          Verificar assinatura
        </Button>
      )}
      {acoesSeguras.includes('visualizar_pdf') && dados && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Eye size={14} weight="bold" />}
          onClick={openPdf}
        >
          Visualizar documento
        </Button>
      )}
      {acoesSeguras.includes('baixar_docx') && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<FileDoc size={14} weight="bold" />}
          onClick={handleDownloadDocx}
          isLoading={downloadingDocx}
        >
          Baixar DOCX
        </Button>
      )}
      {canFinalizar && (
        <Button
          size="sm"
          colorScheme="green"
          leftIcon={<CheckCircle size={14} weight="bold" />}
          onClick={onFinalizar}
          isLoading={finalizando}
        >
          Finalizar processo
        </Button>
      )}
      {!canFinalizar && estatuto.status === 'assinado_finalizado' && dados && (
        <Button
          size="sm"
          colorScheme="green"
          leftIcon={<CheckCircle size={14} weight="bold" />}
          variant="ghost"
          isDisabled
        >
          Finalizado
        </Button>
      )}
      {dados &&
        acoesSeguras.includes('visualizar_pdf') === false &&
        acoesSeguras.includes('baixar_docx') === false &&
        acoesSeguras.includes('ver_dados') && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<DownloadSimple size={14} weight="bold" />}
            onClick={handleDownloadDocx}
            isLoading={downloadingDocx}
          >
            Baixar DOCX (prévia)
          </Button>
        )}
      {estatuto.kanban_card_id != null && (
        <Button
          size="sm"
          variant="ghost"
          colorScheme="gray"
          leftIcon={<Archive size={14} weight="bold" />}
          onClick={handleArquivar}
          isLoading={arquivarMutation.isPending}
        >
          Arquivar
        </Button>
      )}
    </Stack>
  );
};
